import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { ChatPanel } from '../chat/ChatPanel'
import type { UseChatResult } from '../chat/use-chat'
import { ClientAnalyzerMatrix, ClientAnalyzerPro } from '../components/AnalyzerPro'
import { LevelMeterPro } from '../components/LevelMeterPro'
import { LinkCard } from '../components/LinkCard'
import { ProgramFeed } from '../components/ProgramFeed'
import { RecoveryCard } from '../components/RecoveryCard'
import { TimecodeDisplay } from '../components/TimecodeDisplay'
import { VideoGrid } from '../components/VideoGrid'
import {
  DeviceSelect,
  Panel,
  RadioGroupPanel,
  SlpButton,
  StatusBar,
  StatusLamp,
} from '../components/slp'
import { setSessionStatus } from '../lib/api'
import { type JoinHandoff, clearJoinHandoff, loadJoinHandoff } from '../lib/join-store'
import { navigate } from '../lib/router'
import type { RtRole } from '../lib/rt-protocol'
import { getAccessToken } from '../lib/supabase'
import { useAudioLevel } from '../lib/use-audio-level'
import { setOutputDeviceLabel } from '../rt/analyzer-bus'
import { type RecoveredRecording, scanOpfsRecordings } from '../rt/recovery'
import { type UseLocalAnalyzerResult, useLocalAnalyzer } from '../rt/use-analyzer'
import { useRtConnection } from '../rt/use-rt-connection'
import {
  type ProgramSource,
  type RtConnection,
  type RtParticipantInfo,
  type UseRtRoomResult,
  useRtRoom,
} from '../rt/use-rt-room'
import { WorkspacePanel } from '../workspace/WorkspacePanel'
import '../styles/slp.css'

const connLabel: Record<RtConnection, string> = {
  mock: 'Offline demo',
  connecting: 'Connecting…',
  connected: 'Connected',
  reconnecting: 'Reconnecting…',
  disconnected: 'Disconnected',
  failed: 'Connection failed',
}

export function RoomPage() {
  const [handoff] = useState<JoinHandoff | null>(() => loadJoinHandoff())

  if (!handoff) {
    return (
      <div className="min-h-screen min-w-[1180px] bg-[var(--slp-page)]">
        <div className="mx-auto flex min-h-screen w-[1180px] flex-col justify-center">
          <Panel title="No active session" className="mx-auto w-[472px]">
            <p className="text-[12px] text-[var(--slp-text-dim)]">
              Open your invite link again to run the pre-flight check and join.
            </p>
          </Panel>
        </div>
      </div>
    )
  }

  return <RoomInner handoff={handoff} />
}

function RoomInner({ handoff }: { handoff: JoinHandoff }) {
  const rt = useRtRoom(handoff)
  const { join } = handoff
  const role = join.role
  const fps = join.session.frame_rate_fps
  const displayName = handoff.displayName || join.display_name || role

  // v2.4 Client Analyzer sampler — runs on EVERY role (its 8-item summary
  // feeds the 5s heartbeat that builds the studio matrix).
  const analyzer = useLocalAnalyzer({
    room: rt.room,
    role,
    micStream: rt.localMicStream,
    recState: rt.recState,
    recBytes: rt.recBytes,
    programTrack: rt.programTrack,
    getRecorderDiag: rt.getRecorderDiag,
  })

  // G1 v2.11 — the chat hook instance lives at App level
  // (RtConnectionProvider): the pre-flight transcript from /join carries over
  // seamlessly. registerRoom is a stable callback; re-wiring it here swaps
  // the chat onto the in-room Room (and tears down any preflight connection).
  const { chat, registerRoom } = useRtConnection()
  useEffect(() => {
    registerRoom(rt.room, rt.localIdentity, role, displayName)
    return () => registerRoom(null, 'local', role, displayName)
  }, [registerRoom, rt.room, rt.localIdentity, role, displayName])

  // REC start/stop + join/leave → gray system rows in the chat (spec §聊天窗口)
  const prevRecRef = useRef(rt.recState)
  useEffect(() => {
    const prev = prevRecRef.current
    if (prev !== rt.recState) {
      if (rt.recState === 'recording') chat.addSystem(`● REC start — take ${rt.recTake}`)
      else if (prev === 'recording') chat.addSystem(`■ REC stop — take ${rt.recTake}`)
      prevRecRef.current = rt.recState
    }
  }, [rt.recState, rt.recTake, chat])

  const prevIdsRef = useRef<Map<string, string>>(new Map())
  useEffect(() => {
    const prev = prevIdsRef.current
    const cur = new Map(rt.participants.filter((p) => !p.isLocal).map((p) => [p.identity, p.name]))
    for (const [id, name] of cur) if (!prev.has(id) && prev.size > 0) chat.addSystem(`${name} joined`)
    for (const [id, name] of prev) if (!cur.has(id)) chat.addSystem(`${name} left`)
    prevIdsRef.current = cur
  }, [rt.participants, chat])

  const onLeave = () => {
    rt.leave()
    clearJoinHandoff()
    navigate(`/join/${handoff.linkToken}`)
  }

  return (
    // v2.13 locked canvas: fixed 1180px, centered; narrower windows get a
    // horizontal scrollbar — panels NEVER reflow (SLP muscle-memory rule).
    <div className="min-h-screen min-w-[1180px] bg-[var(--slp-page)] pb-[36px]">
      <div className="mx-auto w-[1180px] py-3">
        <div className="mb-3 flex items-center gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[18px] font-semibold tracking-tight text-[var(--slp-text)]">
              {join.session.title}
            </h1>
            <div className="mt-0.5 flex items-center gap-2 text-[12px] text-[var(--slp-text-dim)]">
              <span className="font-semibold uppercase">{role}</span>
              {join.session.project_code ? (
                <span className="font-mono">{join.session.project_code}</span>
              ) : null}
              <span>{fps} fps</span>
              <span className="flex items-center gap-1">
                <StatusLamp
                  status={
                    rt.connection === 'connected' || rt.connection === 'mock'
                      ? 'ok'
                      : rt.connection === 'reconnecting' || rt.connection === 'connecting'
                        ? 'warn'
                        : 'error'
                  }
                  size="sm"
                />
                {connLabel[rt.connection]}
              </span>
            </div>
          </div>
          <SlpButton variant="secondary" size="sm" onClick={onLeave}>
            Leave
          </SlpButton>
        </div>

        {rt.error ? (
          <div className="mb-3 rounded-md border border-[var(--slp-lamp-yellow)] bg-[#fff8e1] px-3 py-2 text-[13px] text-[#7a5d00]">
            {rt.error}
          </div>
        ) : null}

        {role === 'actor' ? (
          <ActorLayout rt={rt} fps={fps} handoff={handoff} chat={chat} analyzer={analyzer} />
        ) : null}
        {role === 'studio' ? <StudioLayout rt={rt} fps={fps} handoff={handoff} chat={chat} /> : null}
        {role === 'director' || role === 'engineer' ? (
          <MonitorLayout rt={rt} fps={fps} chat={chat} role={role} handoff={handoff} analyzer={analyzer} />
        ) : null}
      </div>

      <ChatPanel chat={chat} />
      <StatusBar event={rt.lastEvent} role={role} className="fixed inset-x-0 bottom-0 z-30" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function isOnline(p: RtParticipantInfo): boolean {
  return !(p.statusAtMs !== null && Date.now() - p.statusAtMs > 12_000)
}

/** Who's-watching roster for the program-feed overlay (v2.6): remote names. */
function rosterOf(rt: UseRtRoomResult): string[] {
  return rt.participants.filter((p) => !p.isLocal).map((p) => p.name)
}

/** Leaf component: contains the 60fps level state so pages don't re-render. */
function MicMeter({
  id,
  stream,
  variant = 'horizontal',
  label,
  height,
  className,
}: {
  id: string
  stream: MediaStream | null
  variant?: 'horizontal' | 'vertical'
  label?: string
  height?: number
  className?: string
}) {
  const db = useAudioLevel(stream, { intervalMs: 16 })
  return (
    <LevelMeterPro id={id} peakDbfs={db} variant={variant} label={label} height={height} className={className} />
  )
}

type LampStatus = 'ok' | 'warn' | 'error' | 'off'

/** v2.6 — per-link health for the Connection-row tri-lamp (audio/video/data). */
function linkLamps(p: RtParticipantInfo, rt: UseRtRoomResult): Record<'audio' | 'video' | 'data', LampStatus> {
  if (p.isLocal) {
    return {
      audio: rt.localMicStream ? (rt.micMuted && !rt.pttActive ? 'warn' : 'ok') : 'off',
      video: rt.cameraEnabled ? 'ok' : 'off',
      data:
        rt.connection === 'connected' || rt.connection === 'mock'
          ? 'ok'
          : rt.connection === 'reconnecting' || rt.connection === 'connecting'
            ? 'warn'
            : 'error',
    }
  }
  const age = p.statusAtMs == null ? null : Date.now() - p.statusAtMs
  return {
    audio: rt.remoteAudioStreams[p.identity] ? 'ok' : isOnline(p) ? 'warn' : 'off',
    video: rt.remoteCameras[p.identity] ? 'ok' : 'off',
    data: age == null ? 'off' : age <= 12_000 ? 'ok' : age <= 30_000 ? 'warn' : 'error',
  }
}

/**
 * SLP Connection row — carries the FROZEN e2e contract:
 * data-testid="rt-participant-card" + data-role, one per participant shown.
 * v2.6: the three lamps are real per-link states (audio / video / data);
 * v2.7 #8: hover shows the guest's device echo, actors with AEC on get an
 * amber tag.
 */
function ConnectionRow({
  p,
  rt,
  bitrateKbps,
  onDisconnect,
}: {
  p: RtParticipantInfo
  rt: UseRtRoomResult
  bitrateKbps: number
  onDisconnect?: (identity: string) => void
}) {
  const online = isOnline(p)
  const rtt = p.status?.sum?.rttMs ?? p.status?.net?.rttMs
  const lamps = linkLamps(p, rt)
  const dev = p.status?.dev
  // v2.10 patch-bay: ON AIR = studio ends + patched guests; an unpatched
  // guest sits in the waiting pool (mic muted + unsubscribable) = standby.
  const onAir = p.isLocal || p.role === 'studio' || rt.patchTable[p.identity] != null
  // hover report: device echo + the guest's pre-flight medical (v2.9)
  const gradeLines = p.status?.grades
    ? Object.entries(p.status.grades)
        .map(([k, v]) => `  ${k}: ${v}`)
        .join('\n')
    : ''
  const devTitle =
    [
      dev ? `in: ${dev.inputLabel ?? '—'}\nout: ${dev.outputLabel ?? '—'}\nAEC: ${dev.aec ? 'on' : 'off'}` : '',
      gradeLines ? `pre-flight:\n${gradeLines}` : '',
    ]
      .filter(Boolean)
      .join('\n') || undefined
  const aecAmber = p.role === 'actor' && dev?.aec === true
  return (
    <div
      data-testid="rt-participant-card"
      data-role={p.role}
      data-local={p.isLocal || undefined}
      className="border-b border-[var(--slp-border)] py-1.5 last:border-b-0"
    >
      <div className="flex items-center gap-2">
        <StatusLamp status={online ? 'ok' : 'off'} size="sm" />
        <span
          className="truncate text-[13px] font-semibold text-[var(--slp-text)]"
          title={devTitle}
        >
          {p.name}
          {p.isLocal ? ' (you)' : ''}
        </span>
        <span className="text-[11px] uppercase text-[var(--slp-text-dim)]">{p.role}</span>
        {rtt != null ? (
          <span className="font-mono text-[11px] text-[var(--slp-text-dim)]">[{rtt}ms]</span>
        ) : null}
        {aecAmber ? (
          <span
            className="rounded bg-[#fff3d6] px-1 text-[10px] font-bold text-[#b07b00]"
            title="Actor has echo cancellation ON — recommend OFF for recording"
          >
            AEC ⚠
          </span>
        ) : null}
        <span className="ml-auto flex items-center gap-1" title="audio / video / data links">
          <StatusLamp status={lamps.audio} label={`audio link ${lamps.audio}`} size="sm" />
          <StatusLamp status={lamps.video} label={`video link ${lamps.video}`} size="sm" />
          <StatusLamp status={lamps.data} label={`data link ${lamps.data}`} size="sm" />
        </span>
        {!p.isLocal && onDisconnect ? (
          <SlpButton
            size="sm"
            variant="danger"
            className="ml-1 !px-2 !py-1 text-[10px]"
            onClick={() => onDisconnect(p.identity)}
          >
            Disconnect
          </SlpButton>
        ) : null}
      </div>
      <div
        className={clsx(
          'mt-0.5 pl-5 text-[11px]',
          online && onAir ? 'font-medium text-[var(--slp-lamp-green)]' : 'text-[var(--slp-text-dim)]',
        )}
      >
        {online
          ? onAir
            ? `ON AIR - receive: mono, ${bitrateKbps}kbit/s`
            : 'standby — waiting pool'
          : 'offline'}
        {p.status?.rec?.state === 'recording' ? (
          <span className="ml-2 font-semibold text-[var(--slp-lamp-red)]">● REC</span>
        ) : null}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// v2.10 patch-bay (studio): Remote User slots + waiting-pool cards + remove
// drop zone. Dual interaction paths — HTML5 drag&drop (SLP muscle memory) and
// Connect/Disconnect buttons (a11y + e2e-stable).
// ---------------------------------------------------------------------------

const DRAG_MIME = 'text/plain' // dataTransfer payload = participant identity

/** One ordered Connection slot (Remote User 1..3). */
function PatchSlot({
  n,
  occupant,
  reservedIdentity,
  pool,
  rt,
}: {
  n: number
  /** participant currently patched into this slot (null = empty/reserved) */
  occupant: RtParticipantInfo | null
  /** identity patched here but not currently in the room (crash/reconnect) */
  reservedIdentity: string | null
  pool: RtParticipantInfo[]
  rt: UseRtRoomResult
}) {
  const [hover, setHover] = useState(false)
  const [choice, setChoice] = useState('')

  if (occupant) {
    return (
      <div
        data-testid={`rt-slot-${n}`}
        data-occupied="1"
        data-identity={occupant.identity}
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData(DRAG_MIME, occupant.identity)
          e.dataTransfer.effectAllowed = 'move'
        }}
        className="cursor-grab border-b border-[var(--slp-border)] pb-1 last:border-b-0"
        title="Drag this row to the Remove zone to send the user back to the pool"
      >
        <div className="pt-1 text-[10px] font-bold uppercase tracking-wide text-[var(--slp-text-dim)]">
          Remote User {n}
        </div>
        <ConnectionRow
          p={occupant}
          rt={rt}
          bitrateKbps={rt.audioBitrateKbps}
          onDisconnect={() => rt.unpatchUser(occupant.identity)}
        />
      </div>
    )
  }

  if (reservedIdentity) {
    return (
      <div
        data-testid={`rt-slot-${n}`}
        data-occupied="1"
        data-identity={reservedIdentity}
        className="my-1 rounded border border-dashed border-[var(--slp-border)] px-2 py-1.5"
      >
        <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--slp-text-dim)]">
          Remote User {n}
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--slp-text-dim)]">
          <span className="truncate">waiting for {reservedIdentity} to reconnect…</span>
          <SlpButton
            size="sm"
            variant="secondary"
            className="ml-auto !px-2 !py-1 text-[10px]"
            onClick={() => rt.unpatchUser(reservedIdentity)}
          >
            Clear
          </SlpButton>
        </div>
      </div>
    )
  }

  return (
    <div
      data-testid={`rt-slot-${n}`}
      data-occupied="0"
      data-hover={hover || undefined}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(DRAG_MIME)) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          setHover(true)
        }
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault()
        setHover(false)
        const identity = e.dataTransfer.getData(DRAG_MIME)
        if (identity) rt.patchUser(identity, n)
      }}
      className={clsx(
        'my-1 rounded border border-dashed px-2 py-1.5',
        hover ? 'border-[var(--slp-blue)] bg-[#e9f6fd]' : 'border-[var(--slp-border)]',
      )}
    >
      <div className="text-[10px] font-bold uppercase tracking-wide text-[var(--slp-text-dim)]">
        Remote User {n}
      </div>
      <div
        className={clsx(
          'py-0.5 text-[11px]',
          hover ? 'font-semibold text-[var(--slp-blue)]' : 'text-[var(--slp-text-dim)]',
        )}
      >
        {hover ? 'drop to connect' : 'drag&drop user here to connect'}
      </div>
      <div className="mt-0.5 flex items-center gap-1.5">
        <select
          value={choice}
          onChange={(e) => setChoice(e.target.value)}
          data-testid={`rt-slot-${n}-select`}
          className="h-[26px] min-w-0 flex-1 rounded border border-[var(--slp-border)] bg-[var(--slp-panel)] px-1 text-[11px] text-[var(--slp-text)] focus:border-[var(--slp-blue)] focus:outline-none"
        >
          <option value="">choose from pool…</option>
          {pool.map((p) => (
            <option key={p.identity} value={p.identity}>
              {p.name} ({p.role})
            </option>
          ))}
        </select>
        <SlpButton
          size="sm"
          disabled={!choice}
          onClick={() => {
            if (choice) rt.patchUser(choice, n)
            setChoice('')
          }}
          data-testid={`rt-slot-${n}-connect`}
        >
          Connect
        </SlpButton>
      </div>
    </div>
  )
}

/** Drop target that returns a patched row to the waiting pool. */
function RemoveDropzone({ rt }: { rt: UseRtRoomResult }) {
  const [hover, setHover] = useState(false)
  return (
    <div
      data-testid="rt-remove-dropzone"
      data-hover={hover || undefined}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes(DRAG_MIME)) {
          e.preventDefault()
          e.dataTransfer.dropEffect = 'move'
          setHover(true)
        }
      }}
      onDragLeave={() => setHover(false)}
      onDrop={(e) => {
        e.preventDefault()
        setHover(false)
        const identity = e.dataTransfer.getData(DRAG_MIME)
        if (identity) rt.unpatchUser(identity)
      }}
      className={clsx(
        'mt-2 rounded border border-dashed px-2 py-2 text-center text-[11px]',
        hover
          ? 'border-[var(--slp-lamp-red)] bg-[#fdecec] font-semibold text-[var(--slp-lamp-red)]'
          : 'border-[var(--slp-border)] text-[var(--slp-text-dim)]',
      )}
    >
      Remove User: drag&drop user here to remove
    </div>
  )
}

/** v2.7 #6/#7 — mono tiers drive the ACTOR uplink (cfg broadcast); stereo
 *  tiers drive the program-feed reference audio (★ = SLP-recommended). */
const BITRATE_OPTIONS = [
  { value: '64', label: '64 kbit/s mono' },
  { value: '128', label: '128 kbit/s mono' },
  { value: '192', label: '192 kbit/s mono' },
  { value: '256', label: '256 kbit/s mono' },
  { value: '192s', label: '192 kbit/s stereo (program)' },
  { value: '320s', label: '320 kbit/s stereo (program) ★' },
  { value: '510s', label: '510 kbit/s stereo (program max)' },
  { value: '128lr', label: '128 kbit/s L/R split', disabled: true },
]

/** v2.6 🔊 Loopback — hold to hear your own mic on the monitor output. */
function LoopbackButton({ stream, sinkId }: { stream: MediaStream | null; sinkId: string }) {
  const elRef = useRef<HTMLAudioElement | null>(null)
  const [active, setActive] = useState(false)

  const stop = () => {
    setActive(false)
    const el = elRef.current
    elRef.current = null
    if (el) {
      el.pause()
      el.srcObject = null
    }
  }
  const start = () => {
    if (!stream || elRef.current) return
    const el = new Audio()
    el.srcObject = stream
    el.autoplay = true
    const sinkable = el as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> }
    if (sinkId && typeof sinkable.setSinkId === 'function') {
      void sinkable.setSinkId(sinkId).catch(() => undefined)
    }
    void el.play().catch(() => undefined)
    elRef.current = el
    setActive(true)
  }
  useEffect(() => stop, [])

  return (
    <SlpButton
      size="sm"
      variant={active ? 'danger' : 'secondary'}
      disabled={!stream}
      onPointerDown={start}
      onPointerUp={stop}
      onPointerLeave={() => active && stop()}
      onPointerCancel={stop}
      data-testid="rt-loopback"
      data-active={active || undefined}
      title="Hold to hear your own microphone (mic → headphones)"
    >
      {active ? '🔊 Listening…' : '🔊 Loopback test'}
    </SlpButton>
  )
}

/**
 * v2.6 audio tuning block — Adjust Send Level slider + AEC checkbox +
 * v2.7 #2 speaker-dim checkbox. Copy canonical source:
 * packages/shared/src/copy/rt-audio.ts (en rendered inline, F4 precedent).
 */
function AudioTuning({ rt, isActor }: { rt: UseRtRoomResult; isActor: boolean }) {
  return (
    <div className="flex flex-col gap-2">
      <label className="flex items-center gap-2 text-[12px] text-[var(--slp-text-dim)]">
        <span className="w-24 shrink-0">Send Level</span>
        <input
          type="range"
          min={0}
          max={100}
          step={1}
          value={rt.sendLevelPct}
          onChange={(e) => rt.setSendLevelPct(Number(e.target.value))}
          className="min-w-0 flex-1"
          style={{ accentColor: 'var(--slp-blue)' }}
          data-testid="rt-send-level"
        />
        <span className="w-10 text-right font-mono text-[11px] text-[var(--slp-text)]">
          {rt.sendLevelPct}%
        </span>
      </label>
      <p className="pl-[104px] text-[10px] leading-tight text-[var(--slp-text-dim)]">
        Only changes what others hear live — your local recording keeps the original gain.
      </p>
      <label className="flex items-start gap-2 text-[12px] text-[var(--slp-text)]">
        <input
          type="checkbox"
          checked={rt.aecEnabled}
          onChange={(e) => rt.setAec(e.target.checked)}
          style={{ accentColor: 'var(--slp-blue)' }}
          className="mt-0.5"
          data-testid="rt-aec"
        />
        <span>
          Echo cancellation (AEC)
          {isActor ? (
            <b className="block text-[11px] leading-snug text-[#b07b00]">
              Voice actors: keep this OFF — echo cancellation degrades the recording monitor
              chain.
            </b>
          ) : (
            <span className="block text-[11px] leading-snug text-[var(--slp-text-dim)]">
              Using laptop speakers? Keep it on so the room does not hear itself.
            </span>
          )}
        </span>
      </label>
      <label className="flex items-center gap-2 text-[12px] text-[var(--slp-text)]">
        <input
          type="checkbox"
          checked={rt.speakerDim}
          onChange={(e) => rt.setSpeakerDim(e.target.checked)}
          style={{ accentColor: 'var(--slp-blue)' }}
          data-testid="rt-speaker-dim"
        />
        Dim speakers while talking (−15 dB)
      </label>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Studio — three-column SLP panel grid
// ---------------------------------------------------------------------------
function StudioLayout({
  rt,
  fps,
  handoff,
  chat,
}: {
  rt: UseRtRoomResult
  fps: number
  handoff: JoinHandoff
  chat: UseChatResult
}) {
  const [takeInput, setTakeInput] = useState('1')
  const [statusBusy, setStatusBusy] = useState(false)
  const [sessionStatusLocal, setSessionStatusLocal] = useState(handoff.join.session.status)
  const [bitrateChoice, setBitrateChoice] = useState('128')
  const [prefixDraft, setPrefixDraft] = useState(rt.wavPrefix)
  const [trace, setTrace] = useState(
    () => localStorage.getItem(`rt.trace.${handoff.join.session.id}`) ?? '',
  )
  const [audioDelayMs, setAudioDelayMs] = useState('0')
  // v2.5 program source: remember the picked devices so re-publishes (source
  // toggles, program-audio changes) reuse them.
  const [videoDeviceId, setVideoDeviceId] = useState('')
  const [programAudioId, setProgramAudioId] = useState('')
  const take = Math.max(1, Number.parseInt(takeInput, 10) || 1)

  const applyProgramSource = (src: ProgramSource, audioId = programAudioId) =>
    void rt.setProgramSource(src, {
      videoDeviceId: videoDeviceId || undefined,
      audioDeviceId: audioId || undefined,
    })

  const remotes = rt.participants.filter((p) => !p.isLocal)
  const actorStreamEntry =
    remotes.find((p) => p.role === 'actor' && rt.remoteAudioStreams[p.identity]) ??
    remotes.find((p) => rt.remoteAudioStreams[p.identity])
  const receiveStream = actorStreamEntry ? (rt.remoteAudioStreams[actorStreamEntry.identity] ?? null) : null

  const updateStatus = async (status: 'live' | 'ended') => {
    setStatusBusy(true)
    try {
      const jwt = await getAccessToken().catch(() => null)
      if (jwt) await setSessionStatus(jwt, handoff.join.session.id, status)
      setSessionStatusLocal(status)
    } catch {
      setSessionStatusLocal(status) // local UI state still moves; API is source of truth for S1
    } finally {
      setStatusBusy(false)
    }
  }

  const stopAndIncrement = () => {
    rt.sendRec('stop', take)
    setTakeInput(String(take + 1))
  }

  const applyPrefix = () => rt.setWavPrefix(prefixDraft)

  // v2.7 #6/#7 — mono tiers → actor uplink via cfg broadcast; stereo tiers
  // ('192s'/'320s'/'510s') → program-feed audio sender maxBitrate.
  const onBitrate = (v: string) => {
    setBitrateChoice(v)
    const kbps = Number.parseInt(v, 10)
    if (!Number.isFinite(kbps)) return
    if (v.endsWith('s')) rt.setProgramAudioBitrateKbps(kbps)
    else rt.setActorBitrateKbps(kbps)
  }

  const inputCls =
    'rounded border border-[var(--slp-border)] bg-[var(--slp-panel)] px-2 py-1 text-[13px] text-[var(--slp-text)] focus:border-[var(--slp-blue)] focus:outline-none'

  const studioLinkUrl = `${window.location.origin}/join/${handoff.linkToken}`

  return (
    <div className="flex flex-col gap-3">
      {/* v2.13: three FIXED columns 340/472/340 + 12px gap — no breakpoints */}
      <div className="grid grid-cols-[340px_472px_340px] gap-3">
        {/* ── Left column ─────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          <Panel title="Audio Devices">
            <div className="flex flex-col gap-2">
              <DeviceSelect kind="audioinput" onChange={(id) => void rt.switchDevice('audioinput', id)} />
              <DeviceSelect
                kind="audiooutput"
                onChange={(id, device) => {
                  setOutputDeviceLabel(device?.label ?? null)
                  void rt.switchDevice('audiooutput', id)
                }}
              />
              <div className="flex justify-end">
                <LoopbackButton stream={rt.localMicStream} sinkId={rt.audioOutputId} />
              </div>
              <div className="mt-1 border-t border-[var(--slp-border)] pt-2">
                <div className="flex flex-wrap items-center gap-1.5">
                  <label className="flex items-center gap-1.5 text-[12px] text-[var(--slp-text-dim)]">
                    Take
                    <input
                      type="number"
                      min={1}
                      value={takeInput}
                      onChange={(e) => setTakeInput(e.target.value)}
                      className={clsx(inputCls, 'w-16')}
                      data-testid="rt-rec-take"
                    />
                  </label>
                  <SlpButton
                    size="sm"
                    variant="secondary"
                    onClick={() => rt.sendRec('arm', take)}
                    disabled={rt.recState !== 'idle'}
                    data-testid="rt-rec-arm"
                  >
                    Arm
                  </SlpButton>
                  <SlpButton
                    size="sm"
                    variant="danger"
                    onClick={() => rt.sendRec('start', take)}
                    disabled={rt.recState !== 'armed'}
                    data-testid="rt-rec-start"
                    className={rt.recState === 'recording' ? 'animate-pulse' : undefined}
                  >
                    ● Record
                  </SlpButton>
                  <SlpButton
                    size="sm"
                    variant="secondary"
                    onClick={stopAndIncrement}
                    disabled={rt.recState === 'idle'}
                    data-testid="rt-rec-stop"
                  >
                    ■ Stop
                  </SlpButton>
                </div>
                <div
                  className={clsx(
                    'mt-1.5 text-[12px] font-semibold',
                    rt.recState === 'recording'
                      ? 'text-[var(--slp-lamp-red)]'
                      : rt.recState === 'armed'
                        ? 'text-[#b07b00]'
                        : 'text-[var(--slp-text-dim)]',
                  )}
                >
                  {rt.recState === 'recording'
                    ? `● Recording take ${rt.recTake}`
                    : rt.recState === 'armed'
                      ? `Armed — take ${rt.recTake}`
                      : 'Idle'}
                </div>
              </div>
            </div>
          </Panel>

          <Panel title="Audio Levels">
            <div className="flex flex-col gap-2.5">
              <MicMeter id="studio-send" stream={rt.localMicStream} label="Send" />
              <MicMeter id="studio-receive" stream={receiveStream} label="Receive" />
              <div className="flex items-center gap-2">
                <SlpButton
                  size="sm"
                  variant={rt.micMuted && !rt.pttActive ? 'danger' : 'primary'}
                  onClick={() => rt.setMicMuted(!rt.micMuted)}
                  data-testid="rt-mute-input"
                  className={rt.pttActive ? '!bg-[#e8941a] !text-white' : undefined}
                >
                  {rt.pttActive ? (
                    <span data-testid="rt-ptt-active">ON AIR (PTT)</span>
                  ) : rt.micMuted ? (
                    'Unmute Input'
                  ) : (
                    'Mute Input'
                  )}
                </SlpButton>
                {rt.micMuted && !rt.pttActive ? (
                  <span className="text-[10px] text-[var(--slp-text-dim)]">
                    hold SPACE to talk
                  </span>
                ) : null}
              </div>
              <div className="border-t border-[var(--slp-border)] pt-2">
                <AudioTuning rt={rt} isActor={false} />
              </div>
            </div>
          </Panel>

          <Panel title="Channel Setup: Studio">
            <RadioGroupPanel
              name="bitrate"
              legend="Realtime stream"
              options={BITRATE_OPTIONS}
              value={bitrateChoice}
              onChange={onBitrate}
            />
          </Panel>

          <Panel title="Session Control">
            <div className="flex flex-col gap-2">
              <LinkCard
                link={{ role: 'studio', token: handoff.linkToken, url: studioLinkUrl }}
                label="Studio link"
              />
              <label className="flex flex-col gap-1 text-[12px] text-[var(--slp-text-dim)]">
                Prefix for remote wav files (optional)
                <div className="flex gap-1.5">
                  <input
                    value={prefixDraft}
                    onChange={(e) => setPrefixDraft(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && applyPrefix()}
                    placeholder={handoff.join.session.project_code ?? 'SLP_Rec'}
                    className={clsx(inputCls, 'min-w-0 flex-1 font-mono')}
                    data-testid="rt-wav-prefix"
                  />
                  <SlpButton size="sm" onClick={applyPrefix} data-testid="rt-wav-prefix-apply">
                    Apply
                  </SlpButton>
                </div>
                {rt.wavPrefix ? (
                  <span className="font-mono text-[11px]">
                    active: <span className="font-semibold text-[var(--slp-text)]">{rt.wavPrefix}_…</span>
                  </span>
                ) : null}
              </label>
              <label className="flex flex-col gap-1 text-[12px] text-[var(--slp-text-dim)]">
                Sessions trace
                <input
                  value={trace}
                  onChange={(e) => {
                    setTrace(e.target.value)
                    localStorage.setItem(`rt.trace.${handoff.join.session.id}`, e.target.value)
                  }}
                  className={inputCls}
                />
              </label>
              <div className="flex items-center gap-2 border-t border-[var(--slp-border)] pt-2">
                <StatusLamp status={sessionStatusLocal === 'live' ? 'ok' : 'off'} size="sm" />
                <span className="text-[12px] text-[var(--slp-text-dim)]">{sessionStatusLocal}</span>
                <SlpButton
                  size="sm"
                  disabled={statusBusy || sessionStatusLocal === 'live'}
                  onClick={() => void updateStatus('live')}
                >
                  Start session
                </SlpButton>
                <SlpButton
                  size="sm"
                  variant="secondary"
                  disabled={statusBusy || sessionStatusLocal === 'ended'}
                  onClick={() => void updateStatus('ended')}
                >
                  End
                </SlpButton>
              </div>
            </div>
          </Panel>
        </div>

        {/* ── Middle column ───────────────────────────────────────────── */}
        <div className="flex min-w-0 flex-col gap-3">
          <Panel title="MTC / Video Matrix">
            <div className="flex flex-wrap items-center gap-3">
              <TimecodeDisplay samples={rt.tcSamples} fps={fps} playing={rt.tcPlaying} size="md" />
              <SlpButton size="sm" onClick={rt.tcPlay} disabled={rt.tcPlaying} data-testid="rt-tc-play">
                Play
              </SlpButton>
              <SlpButton
                size="sm"
                variant="secondary"
                onClick={rt.tcStop}
                disabled={!rt.tcPlaying}
                data-testid="rt-tc-stop"
              >
                Stop
              </SlpButton>
            </div>
            <table className="mt-2 w-full border-collapse text-[12px]">
              <thead>
                <tr className="text-left text-[var(--slp-text-dim)]">
                  <th className="py-1 font-semibold">Send to</th>
                  <th className="py-1 text-center font-semibold">Send MTC</th>
                  <th className="py-1 text-center font-semibold">Send Video</th>
                </tr>
              </thead>
              <tbody>
                {remotes.map((p) => (
                  <tr key={p.identity} className="border-t border-[var(--slp-border)]">
                    <td className="py-1 text-[var(--slp-text)]">
                      {p.name} <span className="text-[10px] uppercase text-[var(--slp-text-dim)]">{p.role}</span>
                    </td>
                    <td className="py-1 text-center">
                      <input
                        type="checkbox"
                        style={{ accentColor: 'var(--slp-blue)' }}
                        checked={!rt.tcDeny.includes(p.identity)}
                        onChange={(e) => rt.setTcSend(p.identity, e.target.checked)}
                        data-testid={`rt-matrix-mtc-${p.identity}`}
                      />
                    </td>
                    <td className="py-1 text-center">
                      <input
                        type="checkbox"
                        style={{ accentColor: 'var(--slp-blue)' }}
                        checked={!rt.videoDeny.includes(p.identity)}
                        onChange={(e) => rt.setVideoSend(p.identity, e.target.checked)}
                        data-testid={`rt-matrix-video-${p.identity}`}
                      />
                    </td>
                  </tr>
                ))}
                {remotes.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="py-2 text-[var(--slp-text-dim)]">
                      Waiting for participants…
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </Panel>

          <Panel title="Video Devices">
            <div className="flex flex-col gap-2">
              {/* v2.5 Source selector — Screen Share / Camera·capture card / None */}
              <div
                data-testid="rt-video-source"
                data-source={rt.programSource}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-[var(--slp-text)]"
              >
                <span className="w-24 shrink-0 text-[13px] text-[var(--slp-text-dim)]">Source</span>
                {(
                  [
                    ['screen', 'Screen Share'],
                    ['camera', 'Camera / Capture'],
                    ['none', 'None'],
                  ] as const
                ).map(([value, label]) => (
                  <label key={value} className="flex items-center gap-1.5">
                    <input
                      type="radio"
                      name="rt-program-source"
                      value={value}
                      checked={rt.programSource === value}
                      onChange={() => applyProgramSource(value)}
                      style={{ accentColor: 'var(--slp-blue)' }}
                      data-testid={`rt-video-source-${value}`}
                    />
                    {label}
                  </label>
                ))}
              </div>
              <DeviceSelect
                kind="videoinput"
                onChange={(id) => {
                  setVideoDeviceId(id)
                  void rt.switchDevice('videoinput', id)
                  // capture-card program follows the picked device live
                  if (rt.programSource === 'camera') {
                    void rt.setProgramSource('camera', {
                      videoDeviceId: id || undefined,
                      audioDeviceId: programAudioId || undefined,
                    })
                  }
                }}
              />
              {rt.programSource === 'camera' ? (
                <DeviceSelect
                  kind="audioinput"
                  label="Program audio"
                  testId="rt-program-audio"
                  onChange={(id) => {
                    setProgramAudioId(id)
                    applyProgramSource('camera', id)
                  }}
                />
              ) : null}
              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-1.5 text-[12px] text-[var(--slp-text-dim)]">
                  Video Quality
                  <select
                    value={rt.cameraQuality}
                    onChange={(e) => rt.setCameraQuality(e.target.value as '720p' | '1080p')}
                    className={inputCls}
                    data-testid="rt-video-quality"
                  >
                    <option value="720p">1280×720 @ 30</option>
                    <option value="1080p">1920×1080 @ 5Mbit/s</option>
                  </select>
                </label>
                <label className="flex items-center gap-1.5 text-[12px] text-[var(--slp-text-dim)]">
                  Audio Delay (ms)
                  <input
                    type="number"
                    min={0}
                    step={10}
                    value={audioDelayMs}
                    onChange={(e) => setAudioDelayMs(e.target.value)}
                    className={clsx(inputCls, 'w-20')}
                    data-testid="rt-audio-delay"
                  />
                </label>
                <SlpButton
                  size="sm"
                  variant={rt.programSharing ? 'danger' : 'primary'}
                  onClick={() => applyProgramSource(rt.programSharing ? 'none' : 'screen')}
                  data-testid="rt-share-program"
                >
                  {rt.programSharing ? 'Stop Program' : 'Share Program'}
                </SlpButton>
              </div>
              <div>
                <div className="mb-1 text-[12px] font-semibold text-[var(--slp-text-dim)]">Video Preview</div>
                <ProgramFeed
                  track={rt.localProgramTrack ?? rt.localCameraTrack}
                  tcSamples={rt.tcSamples}
                  fps={fps}
                  tcPlaying={rt.tcPlaying}
                  roster={rosterOf(rt)}
                  rec={rt.recState === 'recording'}
                  withRecTestid
                  placeholder="No video source — camera off and program not shared"
                />
              </div>
            </div>
          </Panel>

          <WorkspacePanel
            linkToken={handoff.linkToken}
            sessionId={handoff.join.session.id}
            role="studio"
            enabled={!handoff.join.mock}
            filesVersion={rt.filesVersion}
            announceFiles={rt.announceFiles}
          />
        </div>

        {/* ── Right column ────────────────────────────────────────────── */}
        <div className="flex flex-col gap-3">
          {/* v2.10 patch-bay: ordered Remote User slots; the studio's own row
              stays on top (always ON AIR — it IS the desk). */}
          <Panel title="Connection">
            {rt.participants
              .filter((p) => p.isLocal)
              .map((p) => (
                <ConnectionRow key={p.identity} p={p} rt={rt} bitrateKbps={rt.audioBitrateKbps} />
              ))}
            {[1, 2, 3].map((n) => {
              const identity =
                Object.entries(rt.patchTable).find(([, slot]) => slot === n)?.[0] ?? null
              const occupant = identity
                ? (rt.participants.find((p) => p.identity === identity && !p.isLocal) ?? null)
                : null
              return (
                <PatchSlot
                  key={n}
                  n={n}
                  occupant={occupant}
                  reservedIdentity={identity && !occupant ? identity : null}
                  pool={remotes.filter(
                    (p) => rt.patchTable[p.identity] == null && p.status?.phase !== 'preflight',
                  )}
                  rt={rt}
                />
              )
            })}
          </Panel>

          {/* v2.10 waiting pool: unpatched guests — drag a card onto a slot
              (or use the slot's Connect button); ✕ = kick out of the session */}
          <Panel title="Available Connections">
            {remotes.filter((p) => rt.patchTable[p.identity] == null).length === 0 ? (
              <p className="text-[12px] text-[var(--slp-text-dim)]">
                {remotes.length === 0
                  ? 'No one else is online yet.'
                  : 'Pool is empty — everyone is patched.'}
              </p>
            ) : (
              <div className="flex flex-col">
                {remotes
                  .filter((p) => rt.patchTable[p.identity] == null)
                  .map((p) => {
                    const pf = p.status?.preflight
                    const inPreflight = p.status?.phase === 'preflight'
                    const grades = p.status?.grades
                    const gradeSummary = grades
                      ? Object.entries(grades)
                          .filter(([k]) => k !== 'overall')
                          .map(([k, v]) => `${k} ${v}`)
                          .join(' · ')
                      : ''
                    return (
                      <div
                        key={p.identity}
                        data-testid={`rt-pool-card-${p.identity}`}
                        data-phase={p.status?.phase ?? undefined}
                        draggable={!inPreflight}
                        onDragStart={(e) => {
                          if (inPreflight) return
                          e.dataTransfer.setData(DRAG_MIME, p.identity)
                          e.dataTransfer.effectAllowed = 'move'
                        }}
                        className={clsx(
                          'border-b border-[var(--slp-border)] py-1.5 last:border-b-0',
                          !inPreflight && 'cursor-grab',
                        )}
                        title={
                          inPreflight
                            ? 'Still in pre-flight — patchable once in the room'
                            : 'Drag onto a Remote User slot to connect'
                        }
                      >
                        <div className="flex items-center gap-2">
                          {isOnline(p) ? (
                            <StatusLamp status="ok" size="sm" />
                          ) : (
                            <span className="text-[12px] font-bold leading-none text-[var(--slp-lamp-red)]">✕</span>
                          )}
                          <span className="truncate text-[12px] text-[var(--slp-text)]">{p.name}</span>
                          <span className="text-[10px] uppercase text-[var(--slp-text-dim)]">{p.role}</span>
                          {inPreflight ? (
                            <span
                              data-testid={`rt-preflight-badge-${p.identity}`}
                              className="rounded bg-[#fff3d6] px-1 text-[9px] font-bold tracking-wide text-[#b07b00]"
                              title="Guest is on the /join page running pre-flight checks"
                            >
                              PRE-FLIGHT
                            </span>
                          ) : null}
                          {pf ? (
                            <StatusLamp
                              status={pf === 'green' ? 'ok' : pf === 'yellow' ? 'warn' : 'error'}
                              label={`pre-flight ${pf}`}
                              size="sm"
                            />
                          ) : null}
                          <SlpButton
                            size="sm"
                            variant="secondary"
                            className="ml-auto !px-2 !py-1 text-[10px]"
                            onClick={() => chat.openTo(p.identity)}
                            data-testid={`rt-open-chat-${p.identity}`}
                          >
                            chat
                          </SlpButton>
                          <SlpButton
                            size="sm"
                            variant="danger"
                            className="!px-2 !py-1 text-[10px]"
                            onClick={() => rt.disconnectParticipant(p.identity)}
                            data-testid={`rt-pool-kick-${p.identity}`}
                            title="Remove this user from the session"
                          >
                            ✕
                          </SlpButton>
                        </div>
                        {inPreflight && gradeSummary ? (
                          <div
                            className="mt-0.5 truncate pl-5 font-mono text-[10px] text-[var(--slp-text-dim)]"
                            title={gradeSummary}
                          >
                            {gradeSummary}
                            {grades?.overall ? (
                              <span
                                className={clsx(
                                  'ml-1 font-bold',
                                  grades.overall === 'POOR'
                                    ? 'text-[var(--slp-lamp-red)]'
                                    : grades.overall === 'OK'
                                      ? 'text-[#b07b00]'
                                      : 'text-[var(--slp-lamp-green)]',
                                )}
                              >
                                ⇒ {grades.overall}
                              </span>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    )
                  })}
              </div>
            )}
            <RemoveDropzone rt={rt} />
          </Panel>

          {/* v2.4 — all-participants matrix (8 heartbeat items + Device/AEC) */}
          <ClientAnalyzerMatrix participants={rt.participants} />
        </div>
      </div>

      <VideoGrid
        participants={rt.participants}
        localCameraTrack={rt.localCameraTrack}
        localMicStream={rt.localMicStream}
        remoteCameras={rt.remoteCameras}
        remoteAudioStreams={rt.remoteAudioStreams}
        cameraEnabled={rt.cameraEnabled}
        onToggleCamera={rt.setCameraOn}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Actor — program feed + big ON AIR + vertical pro meter + takes/recovery
// ---------------------------------------------------------------------------
function formatMb(bytes: number): string {
  return `${(bytes / 1_048_576).toFixed(1)} MB`
}

const takeStatusLabel: Record<string, string> = {
  saving: 'finalizing…',
  registering: 'registering…',
  uploading: 'uploading',
  uploaded: 'done',
  failed: 'failed',
  'local-only': 'saved locally',
}

/** Existing take upload queue, unchanged — now lives in Workspace → Takes. */
function TakesQueue({ takes }: { takes: UseRtRoomResult['takes'] }) {
  if (takes.length === 0) {
    return (
      <p className="text-[12px] text-[var(--slp-text-dim)]">
        No takes yet — recordings appear here and upload automatically.
      </p>
    )
  }
  return (
    <ul className="flex flex-col gap-1.5">
      {takes.map((t) => (
        <li
          key={`${t.take}-${t.fileName}`}
          data-testid="rt-take-item"
          data-status={t.status}
          data-take-id={t.takeId ?? ''}
          className="flex items-center justify-between gap-3 rounded border border-[var(--slp-border)] bg-[var(--slp-panel-2)] px-2.5 py-1.5"
        >
          <div className="min-w-0">
            <span className="font-mono text-[13px] font-semibold text-[var(--slp-text)]">
              T{String(t.take).padStart(2, '0')}
            </span>
            <span className="ml-2 text-[11px] text-[var(--slp-text-dim)]">{formatMb(t.bytes)}</span>
            <div className="truncate font-mono text-[10px] text-[var(--slp-text-dim)]">{t.fileName}</div>
          </div>
          <span
            className={clsx(
              'shrink-0 text-[12px] font-semibold',
              t.status === 'uploaded' && 'text-[var(--slp-lamp-green)]',
              t.status === 'failed' && 'text-[var(--slp-lamp-red)]',
              (t.status === 'uploading' || t.status === 'registering' || t.status === 'saving') &&
                'text-[var(--slp-text-dim)]',
            )}
          >
            {t.status === 'uploading'
              ? `⬆ ${t.progressPct}%`
              : t.status === 'uploaded'
                ? '✓ done'
                : (takeStatusLabel[t.status] ?? t.status)}
          </span>
        </li>
      ))}
    </ul>
  )
}

function ActorLayout({
  rt,
  fps,
  handoff,
  chat,
  analyzer,
}: {
  rt: UseRtRoomResult
  fps: number
  handoff: JoinHandoff
  chat: UseChatResult
  analyzer: UseLocalAnalyzerResult
}) {
  const [pickBusy, setPickBusy] = useState(false)
  const [recovered, setRecovered] = useState<RecoveredRecording[]>([])

  // Scan OPFS for crash survivors ONCE, on mount — before this session
  // records anything (fresh takes are therefore never misreported).
  useEffect(() => {
    if (handoff.join.mock) return
    let cancelled = false
    void scanOpfsRecordings().then((found) => {
      if (!cancelled) setRecovered(found)
    })
    return () => {
      cancelled = true
    }
  }, [handoff.join.mock])

  const onChooseFolder = async () => {
    setPickBusy(true)
    try {
      await rt.chooseSaveFolder()
    } finally {
      setPickBusy(false)
    }
  }

  const remotes = rt.participants.filter((p) => !p.isLocal)
  const selfRtt = rt.participants.find((p) => p.isLocal)?.status?.net?.rttMs

  return (
    // v2.13 locked two-column actor grid: 824 + 340 + 12px gap = 1176
    <div className="grid grid-cols-[824px_340px] gap-3">
      <div className="flex min-w-0 flex-col gap-3">
        <ProgramFeed
          track={rt.studioVideoTrack}
          tcSamples={rt.tcSamples}
          fps={fps}
          tcPlaying={rt.tcPlaying}
          roster={rosterOf(rt)}
          rec={rt.recState === 'recording'}
          placeholder="Waiting for studio program…"
          withTcTestid
        />

        <VideoGrid
          participants={rt.participants}
          localCameraTrack={rt.localCameraTrack}
          localMicStream={rt.localMicStream}
          remoteCameras={rt.remoteCameras}
          remoteAudioStreams={rt.remoteAudioStreams}
          cameraEnabled={rt.cameraEnabled}
          onToggleCamera={rt.setCameraOn}
        />

        <WorkspacePanel
          linkToken={handoff.linkToken}
          sessionId={handoff.join.session.id}
          role="actor"
          enabled={!handoff.join.mock}
          filesVersion={rt.filesVersion}
          announceFiles={rt.announceFiles}
          takesContent={
            <>
              <TakesQueue takes={rt.takes} />
              {recovered.length > 0 ? (
                <RecoveryCard files={recovered} linkToken={handoff.linkToken} />
              ) : null}
            </>
          }
        />
      </div>

      <div className="flex flex-col gap-3">
        <Panel title="Welcome">
          <div data-testid="rt-welcome" className="flex flex-col gap-1.5 text-[12px] text-[var(--slp-text-dim)]">
            <p>Welcome to your remote recording session — here's the short tour:</p>
            <ol className="flex list-decimal flex-col gap-1 pl-4">
              <li>
                Pick your microphone under <b className="text-[var(--slp-text)]">Audio Devices</b> and
                watch the meter move while you speak.
              </li>
              <li>
                The studio starts and stops every take — when the panel shows{' '}
                <b className="text-[var(--slp-lamp-red)]">ON AIR</b>, you are being recorded.
              </li>
              <li>
                Your script lives in <b className="text-[var(--slp-text)]">Workspace → Script</b>; use
                "Open in new window" if you have a second screen.
              </li>
              <li>Recordings save on this computer first, then upload by themselves — just keep this tab open.</li>
            </ol>
            <button
              type="button"
              onClick={() => {
                const studio = rt.participants.find((p) => p.role === 'studio' && !p.isLocal)
                if (studio) chat.openTo(studio.identity)
                else chat.setOpen(true)
              }}
              data-testid="rt-welcome-chat"
              className="self-start text-[12px] font-semibold text-[var(--slp-blue)] hover:underline"
            >
              Click here to start a chat with the studio →
            </button>
          </div>
        </Panel>

        <Panel title="Connection">
          <div
            data-testid="rt-rec-light"
            data-state={rt.recState}
            data-patched={rt.selfPatched || undefined}
            aria-live="polite"
            className={clsx(
              // v2.13: big status word fixed at 28px / 800
              'flex items-center justify-center rounded-md border-2 py-3 text-center text-[28px] font-extrabold tracking-[0.2em]',
              rt.recState === 'recording'
                ? 'animate-pulse border-[var(--slp-lamp-red)] bg-[var(--slp-lamp-red)] text-white'
                : rt.recState === 'armed'
                  ? 'border-[var(--slp-lamp-yellow)] bg-[#fff6da] text-[#8a6d00]'
                  : rt.selfPatched
                    ? // v2.10: patched + idle = live mic line open — red ON AIR
                      'border-[var(--slp-lamp-red)] bg-[var(--slp-panel-2)] text-[var(--slp-lamp-red)]'
                    : 'border-[var(--slp-border)] bg-[var(--slp-panel-2)] text-[var(--slp-text-dim)]',
            )}
          >
            {rt.recState === 'recording'
              ? 'ON AIR'
              : rt.recState === 'armed'
                ? 'ARMED'
                : rt.selfPatched
                  ? 'ON AIR'
                  : 'STANDBY'}
          </div>
          <div className="mt-1.5 text-center text-[11px] text-[var(--slp-text-dim)]">
            {rt.selfPatched
              ? `Connected — Remote User ${rt.patchTable[rt.localIdentity] ?? ''} · `
              : 'Waiting pool — the studio will connect you · '}
            Take {rt.recTake}
            {selfRtt != null ? ` · [${selfRtt}ms]` : ''} · receive channel setup: mono,{' '}
            {rt.audioBitrateKbps}kbit/s
          </div>
          <div className="mt-2 border-t border-[var(--slp-border)] pt-1">
            {remotes.map((p) => {
              const lamps = linkLamps(p, rt)
              return (
                <div
                  key={p.identity}
                  data-testid="rt-participant-card"
                  data-role={p.role}
                  className="flex items-center gap-2 border-b border-[var(--slp-border)] py-1.5 last:border-b-0"
                >
                  <StatusLamp status={isOnline(p) ? 'ok' : 'off'} size="sm" />
                  <span className="truncate text-[13px] font-medium text-[var(--slp-text)]">{p.name}</span>
                  <span className="text-[10px] uppercase text-[var(--slp-text-dim)]">{p.role}</span>
                  <span className="ml-auto flex items-center gap-1" title="audio / video / data links">
                    <StatusLamp status={lamps.audio} label={`audio link ${lamps.audio}`} size="sm" />
                    <StatusLamp status={lamps.video} label={`video link ${lamps.video}`} size="sm" />
                    <StatusLamp status={lamps.data} label={`data link ${lamps.data}`} size="sm" />
                  </span>
                </div>
              )
            })}
          </div>
        </Panel>

        <Panel title="Audio Devices / Record">
          <DeviceSelect kind="audioinput" onChange={(id) => void rt.switchDevice('audioinput', id)} />
          <DeviceSelect
            kind="audiooutput"
            className="mt-2"
            onChange={(id, device) => {
              setOutputDeviceLabel(device?.label ?? null)
              void rt.switchDevice('audiooutput', id)
            }}
          />
          <div className="mt-2 flex justify-end">
            <LoopbackButton stream={rt.localMicStream} sinkId={rt.audioOutputId} />
          </div>
          <div className="mt-2 flex justify-center">
            <MicMeter id="actor-mic" stream={rt.localMicStream} variant="vertical" height={260} label="Mic" />
          </div>
          <p className="mt-1.5 text-center text-[11px] text-[var(--slp-text-dim)]">
            Raw signal — browser processing is off for recording. REF −18 dBFS.
          </p>
          <div className="mt-2 border-t border-[var(--slp-border)] pt-2">
            <AudioTuning rt={rt} isActor />
          </div>
          <div className="mt-2 border-t border-[var(--slp-border)] pt-2">
            <SlpButton
              size="sm"
              variant="secondary"
              disabled={pickBusy || rt.recState === 'recording'}
              onClick={() => void onChooseFolder()}
              data-testid="rt-choose-dir"
            >
              Choose save folder
            </SlpButton>
            <p
              className="mt-1.5 text-[11px] text-[var(--slp-text-dim)]"
              data-testid="rt-storage-mode"
              data-mode={rt.storageMode}
            >
              {rt.storageMode === 'user-dir'
                ? 'Recordings save into your selected folder.'
                : rt.storageMode === 'opfs'
                  ? 'Recordings save to browser secure storage.'
                  : 'Default: browser secure storage. Pick a folder to keep files yourself.'}
            </p>
          </div>
        </Panel>

        <ClientAnalyzerPro
          analyzer={analyzer}
          role="actor"
          targetBitrateKbps={rt.audioBitrateKbps}
        />

        <SlpButton onClick={() => chat.setOpen(true)} data-testid="rt-open-chat">
          Open Chat
        </SlpButton>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Director / Engineer — program + grid + talkback + participants
// ---------------------------------------------------------------------------
function MonitorLayout({
  rt,
  fps,
  chat,
  role,
  handoff,
  analyzer,
}: {
  rt: UseRtRoomResult
  fps: number
  chat: UseChatResult
  role: RtRole
  handoff: JoinHandoff
  analyzer: UseLocalAnalyzerResult
}) {
  const [talking, setTalking] = useState(false)

  // v2.7 #2 — talkback rides the rt hook so local monitoring ducks −15 dB
  const startTalk = () => {
    setTalking(true)
    rt.setTalkback(true)
  }
  const stopTalk = () => {
    setTalking(false)
    rt.setTalkback(false)
  }

  return (
    // v2.13 locked two-column monitor grid: 824 + 340 + 12px gap = 1176
    <div className="grid grid-cols-[824px_340px] gap-3">
      <div className="flex min-w-0 flex-col gap-3">
        <ProgramFeed
          track={rt.studioVideoTrack}
          tcSamples={rt.tcSamples}
          fps={fps}
          tcPlaying={rt.tcPlaying}
          roster={rosterOf(rt)}
          rec={rt.recState === 'recording'}
          withRecTestid
          placeholder="Waiting for studio program…"
        />
        <VideoGrid
          participants={rt.participants}
          localCameraTrack={rt.localCameraTrack}
          localMicStream={rt.localMicStream}
          remoteCameras={rt.remoteCameras}
          remoteAudioStreams={rt.remoteAudioStreams}
          cameraEnabled={rt.cameraEnabled}
          onToggleCamera={rt.setCameraOn}
        />
        <WorkspacePanel
          linkToken={handoff.linkToken}
          sessionId={handoff.join.session.id}
          role={role}
          enabled={!handoff.join.mock}
          filesVersion={rt.filesVersion}
          announceFiles={rt.announceFiles}
        />
        <p className="text-[12px] text-[var(--slp-text-dim)]">
          You're monitoring the actor's live microphone feed.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <Panel title="Timecode">
          <TimecodeDisplay samples={rt.tcSamples} fps={fps} playing={rt.tcPlaying} size="md" />
          <div
            className={clsx(
              'mt-2 text-[13px] font-semibold',
              rt.recState === 'recording'
                ? 'text-[var(--slp-lamp-red)]'
                : rt.recState === 'armed'
                  ? 'text-[#b07b00]'
                  : 'text-[var(--slp-text-dim)]',
            )}
          >
            {rt.recState === 'recording'
              ? `● ON AIR — take ${rt.recTake}`
              : rt.recState === 'armed'
                ? `Armed — take ${rt.recTake}`
                : 'Standby'}
          </div>
        </Panel>

        <Panel title="Talkback">
          <SlpButton
            variant={talking || rt.pttActive ? 'danger' : 'primary'}
            className={clsx('!h-12 w-full select-none', rt.pttActive && '!bg-[#e8941a]')}
            onPointerDown={startTalk}
            onPointerUp={stopTalk}
            onPointerLeave={() => talking && stopTalk()}
            onPointerCancel={stopTalk}
            data-testid="rt-talkback"
            data-active={talking || rt.pttActive || undefined}
          >
            {rt.pttActive ? (
              <span data-testid="rt-ptt-active">● ON AIR (PTT) — release SPACE to mute</span>
            ) : talking ? (
              '● TALKING — release to mute'
            ) : (
              'TALKBACK — hold to talk'
            )}
          </SlpButton>
          <p className="mt-1.5 text-[11px] text-[var(--slp-text-dim)]">
            Your mic stays muted ({role}); hold the button — or hold SPACE — to speak to the room.
          </p>
          <div className="mt-2 border-t border-[var(--slp-border)] pt-2">
            <AudioTuning rt={rt} isActor={false} />
          </div>
        </Panel>

        {/* v2.7 #6 — director/engineer uplink is fixed; no tier choice here */}
        <Panel title={`Channel Setup: ${role === 'director' ? 'Director' : 'Engineer'}`}>
          <div
            data-testid="rt-bitrate-fixed"
            className="rounded border border-[var(--slp-border)] bg-[var(--slp-panel-2)] px-2.5 py-1.5 text-[12px] text-[var(--slp-text-dim)]"
          >
            <span className="font-mono font-semibold">64 kbit/s mono</span> — fixed for {role}{' '}
            talkback (not adjustable)
          </div>
        </Panel>

        <Panel title="Connection">
          {rt.participants.map((p) => (
            <ConnectionRow key={p.identity} p={p} rt={rt} bitrateKbps={rt.audioBitrateKbps} />
          ))}
        </Panel>

        <ClientAnalyzerPro analyzer={analyzer} role={role} targetBitrateKbps={64} />

        <SlpButton variant="secondary" onClick={() => chat.setOpen(true)} data-testid="rt-open-chat">
          Open Chat
        </SlpButton>
      </div>
    </div>
  )
}
